import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { C2Button } from '../components/c2';
import ProgressRail, { type OnboardingStep } from '../components/onboarding/ProgressRail';
import StepFrame from '../components/onboarding/StepFrame';
import ChromeToolbarVisual from '../components/onboarding/ChromeToolbarVisual';
import ExtensionPopupVisual from '../components/onboarding/ExtensionPopupVisual';
import InviteStepBody from '../components/onboarding/InviteStepBody';
import { api, getToken, type User } from '../lib/api';
import { extensionSignInState, hasExtension, waitForExtension } from '../lib/extensionPresence';
import { track } from '../lib/analytics';

const CREATOR_STEPS: OnboardingStep[] = [
  { id: 'workspace', label: 'Your workspace' },
  { id: 'install', label: 'Install the extension' },
  { id: 'pin', label: 'Pin it to your toolbar' },
  { id: 'signin', label: 'Sign in to the extension' },
  { id: 'firstlink', label: 'Create your first go/ link' },
  { id: 'invite', label: 'Invite your team' },
];

const JOINER_STEPS: OnboardingStep[] = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'install', label: 'Install the extension' },
  { id: 'pin', label: 'Pin it to your toolbar' },
  { id: 'signin', label: 'Sign in to the extension' },
  { id: 'firstlink', label: 'Create your first go/ link' },
];

// Re-check flow for users who already finished onboarding but show up on a
// browser without the extension (or never signed in on the popup). No
// workspace/welcome/invite — just get the extension wired up.
const FIX_EXTENSION_STEPS: OnboardingStep[] = [
  { id: 'install', label: 'Install the extension' },
  { id: 'pin', label: 'Pin it to your toolbar' },
  { id: 'signin', label: 'Sign in to the extension' },
];

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid var(--color-hairline-strong)',
  borderRadius: 14,
  fontSize: 14,
  color: 'var(--color-ink)',
  background: 'var(--color-surface)',
  fontFamily: 'inherit',
  outline: 'none',
};

interface StepProps {
  user: User;
  onAdvance: () => void;
  stepIdx: number;
  stepCount: number;
}

// Wraps a (potentially disabled) button so hovering reveals why it can't be
// pressed yet. `reason` is null when the button is enabled.
function DisabledHint({ reason, children }: { reason: string | null; children: ReactNode }) {
  if (!reason) return <>{children}</>;
  return (
    <span className="relative inline-flex group" style={{ verticalAlign: 'middle' }}>
      {children}
      <span
        role="tooltip"
        className="absolute left-1/2 whitespace-nowrap font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{
          bottom: '100%',
          transform: 'translate(-50%, -8px)',
          background: 'var(--color-ink)',
          color: '#fff',
          padding: '6px 12px',
          borderRadius: 999,
          fontSize: 12,
          boxShadow: 'var(--shadow-md)',
          zIndex: 10,
        }}
      >
        {reason}
        <span
          className="absolute left-1/2"
          style={{
            top: '100%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid var(--color-ink)',
          }}
        />
      </span>
    </span>
  );
}

interface DetectedStepProps extends StepProps {
  detected: boolean;
  setDetected: (v: boolean) => void;
}

// Hand-drawn arrow that swoops from inside the page up to the very top edge,
// telling the user to look at their actual Chrome toolbar (above the page).
// Used on the pin / sign-in / first-link steps — wherever we need to redirect
// attention from the page content to the real toolbar.
function OnboardingArrow({ label }: { label: ReactNode }) {
  return (
    <div
      className="absolute hidden md:block"
      style={{
        top: 0,
        right: 182,
        width: 240,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      <div style={{ animation: 'c2arrowAttention 5s ease-in-out 1.2s infinite' }}>
        <svg
          width="200"
          height="110"
          viewBox="0 0 200 110"
          fill="none"
          style={{ marginLeft: 'auto', display: 'block' }}
        >
          <path
            d="M 104 102 C 129 94, 154 74, 167 44 S 188 18, 188 8"
            stroke="var(--color-ink)"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
            style={{ animation: 'c2arrowDraw 700ms ease-out both' }}
          />
          <path
            d="M 178 16 L 188 8 L 191 21"
            stroke="var(--color-ink)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            style={{ animation: 'c2arrowHead 200ms ease-out 600ms both' }}
          />
        </svg>
      </div>
      <div
        style={{
          fontSize: 17,
          textAlign: 'right',
          color: 'var(--color-ink)',
          transform: 'rotate(-4deg)',
          lineHeight: 1.3,
          fontStyle: 'italic',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          marginTop: -2,
        }}
      >
        {label}
      </div>
      <style>{`
        @keyframes c2arrowDraw {
          from { stroke-dashoffset: 220; stroke-dasharray: 220; }
          to { stroke-dashoffset: 0; stroke-dasharray: 220; }
        }
        @keyframes c2arrowHead {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes c2arrowAttention {
          0%, 70% { opacity: 1; }
          85% { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// Workspace step (creator only)
// ============================================================

function WorkspaceStep({ user, onAdvance, stepIdx, stepCount }: StepProps) {
  const domain = user.email.split('@')[1] ?? 'your-company.com';
  const firstName = (user.name ?? user.email.split('@')[0] ?? 'there').split(' ')[0];
  const isPersonalDomain = !!user.workspace?.is_personal
    || ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'].includes(domain.toLowerCase());
  const defaultName = isPersonalDomain
    ? `${firstName}'s personal workspace`
    : (domain.split('.')[0] ?? 'Workspace').replace(/^./, c => c.toUpperCase());
  // Backend auto-generates a placeholder name on workspace creation. Replace it
  // with a friendlier suggestion; preserve anything else as a real customization.
  const savedName = user.workspace?.ws_name?.trim() ?? '';
  const autoPlaceholders = [
    `${user.email.split('@')[0]}'s links`,
    domain,
  ];
  const initialName = !savedName || autoPlaceholders.includes(savedName) ? defaultName : savedName;
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const body = isPersonalDomain
    ? `We spun one up for your personal use.`
    : `We spun one up for ${domain}. Anyone with an @${domain} email auto-joins.`;

  const handleContinue = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.updateWorkspace({ name: trimmed });
    } catch {
      // Non-blocking: continue even if save fails so user isn't stuck.
    } finally {
      setSaving(false);
      onAdvance();
    }
  };

  return (
    <StepFrame
      eyebrow={`Step ${stepIdx + 1} of ${stepCount}`}
      title="Welcome! Let's set up your workspace."
      body={body}
      primaryCta={
        <DisabledHint reason={!name.trim() ? 'Enter a workspace name first' : null}>
          <C2Button variant="accent" size="lg" onClick={handleContinue} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : 'Continue →'}
          </C2Button>
        </DisabledHint>
      }
    >
      <div
        className="bg-surface"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 20,
          padding: 32,
          maxWidth: 560,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div className="flex items-center mb-7" style={{ gap: 14 }}>
          <div
            className="flex items-center justify-center font-display font-bold"
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: 'var(--color-peach)',
              color: 'var(--color-peach-deep)',
              fontSize: 26,
            }}
          >
            {(name || 'A')[0]?.toUpperCase()}
          </div>
          <div>
            <div
              className="font-bold uppercase mb-1"
              style={{ fontSize: 11.5, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
            >
              Your workspace
            </div>
            <div className="text-[15px] text-ink-soft">
              {isPersonalDomain
                ? <>Just for you, signed in as <b className="text-ink font-semibold">{user.email}</b></>
                : <>Auto-created for <b className="text-ink font-semibold">{domain}</b></>}
            </div>
          </div>
        </div>
        <label className="block text-[12.5px] font-semibold text-ink mb-1.5">Workspace name</label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={defaultName}
          style={inputStyle}
          autoFocus
        />
        <p className="text-xs text-ink-muted mt-2">
          {isPersonalDomain
            ? 'You can change this later from your dashboard.'
            : `Teammates with an @${domain} email will see this name when they join. You can change it later from your dashboard.`}
        </p>
      </div>
    </StepFrame>
  );
}

// ============================================================
// Welcome step (joiner only)
// ============================================================

function WelcomeStep({ user, onAdvance, stepCount }: StepProps) {
  const domain = user.email.split('@')[1] ?? 'your-company.com';
  const firstName = (user.name ?? user.email.split('@')[0] ?? 'there').split(' ')[0];
  const savedName = user.workspace?.ws_name?.trim() ?? '';
  // If the creator never renamed the workspace, the backend auto-default is just
  // the bare domain (e.g. "acme.com"). Show a friendlier derived name instead.
  const niceFallback = (domain.split('.')[0] ?? 'Workspace').replace(/^./, c => c.toUpperCase());
  const workspaceName = !savedName || savedName === domain ? niceFallback : savedName;
  return (
    <StepFrame
      eyebrow={`Step 1 of ${stepCount}`}
      title={`Welcome, ${firstName}.`}
      body={`Your team's already on Tidy Go Links at @${domain}. We'll get you the extension, sign you in, and you'll be using go/ links in under a minute.`}
      primaryCta={<C2Button variant="accent" size="lg" onClick={onAdvance}>Let's go →</C2Button>}
    >
      <div
        className="bg-surface grid items-stretch"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 20,
          padding: 28,
          gridTemplateColumns: '1fr 1fr',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: 20,
            background: 'var(--color-bg)',
            borderRadius: 14,
            border: '1px solid var(--color-hairline)',
          }}
        >
          <div
            className="font-bold uppercase mb-2.5"
            style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
          >
            Your workspace
          </div>
          <div className="flex items-center gap-3 mb-3.5">
            <div
              className="font-display font-bold flex items-center justify-center"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                fontSize: 18,
              }}
            >
              {workspaceName[0]?.toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-ink text-[15px]">{workspaceName}</div>
              <div className="text-xs text-ink-muted font-mono">@{domain}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <div
            className="font-bold uppercase mb-3"
            style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
          >
            What's next
          </div>
          <div className="flex flex-col gap-2.5">
            {['Install the Chrome extension', 'Pin it to your toolbar', 'Sign in', 'Create your first go/ link'].map((label, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-ink-soft">
                <div
                  className="rounded-full flex items-center justify-center font-mono font-bold shrink-0"
                  style={{
                    width: 22,
                    height: 22,
                    background: 'var(--color-bg-alt)',
                    color: 'var(--color-ink-muted)',
                    fontSize: 11,
                  }}
                >
                  {i + 1}
                </div>
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </StepFrame>
  );
}

// ============================================================
// Install step
// ============================================================

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/tidy-go-links/nijbmhklldgocejmopojcapjiacnepja';

function InstallStep({ onAdvance, detected, setDetected, stepIdx, stepCount }: DetectedStepProps) {
  // Watch for the extension's content-script marker on <html>. If the user
  // already has it installed when they land here, fire immediately; otherwise
  // poll for ~30s in case they install in another tab and come back.
  useEffect(() => {
    if (detected) return;
    if (hasExtension()) {
      setDetected(true);
      return;
    }
    let cancelled = false;
    void waitForExtension(30_000).then(found => {
      if (!cancelled && found) setDetected(true);
    });
    return () => {
      cancelled = true;
    };
  }, [detected, setDetected]);

  return (
    <StepFrame
      eyebrow={`Step ${stepIdx + 1} of ${stepCount}`}
      title="Install the Chrome extension."
      body="Go/ links only work if the extension is installed."
      primaryCta={
        <DisabledHint reason={!detected ? 'Install the extension first' : null}>
          <C2Button
            variant={detected ? 'accent' : 'soft'}
            size="lg"
            onClick={detected ? onAdvance : undefined}
            disabled={!detected}
            style={!detected ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          >
            Continue →
          </C2Button>
        </DisabledHint>
      }
      secondaryCta={
        !detected ? (
          <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" className="no-underline">
            <C2Button variant="accent" size="lg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Install extension
            </C2Button>
          </a>
        ) : null
      }
      detected={detected}
      detectedLabel="Extension installed"
    >
      <div
        className="bg-surface mx-auto"
        style={{
          maxWidth: 720,
          border: '1px solid var(--color-hairline)',
          borderRadius: 20,
          padding: '28px 32px',
          boxShadow: 'var(--shadow-md)',
          display: 'grid',
          gridTemplateColumns: '80px 1fr',
          gap: 22,
          alignItems: 'start',
        }}
      >
        <div
          className="font-mono font-extrabold flex items-center justify-center"
          style={{
            width: 80,
            height: 80,
            borderRadius: 20,
            background: 'var(--accent-soft)',
            color: 'var(--accent-deep)',
            fontSize: 26,
            border: '1px solid color-mix(in oklch, var(--accent-solid) 20%, transparent)',
          }}
        >
          go/
        </div>
        <div>
          <div
            className="font-display font-bold text-ink mb-1"
            style={{ fontSize: 24, letterSpacing: '-0.01em' }}
          >
            Tidy Go Links
          </div>
          <div className="text-[13px] text-ink-soft mb-2.5">
            <b>Free Code Collective</b> ·{' '}
            <span className="font-medium" style={{ color: 'var(--accent-deep)' }}>Featured</span>
          </div>
          <div className="flex items-center gap-3.5 text-xs text-ink-muted mb-4">
            <span className="flex items-center gap-1">
              <span style={{ color: 'oklch(0.7 0.15 80)' }}>★★★★★</span>
              <b className="text-ink">4.9</b> (2,184)
            </span>
            <span>·</span>
            <span>3,200+ users</span>
            <span>·</span>
            <span>Productivity</span>
          </div>
          <div
            className="inline-flex items-center gap-1.5 font-medium"
            style={{
              padding: '9px 20px',
              background: detected ? 'oklch(0.72 0.14 150)' : 'oklch(0.55 0.18 250)',
              color: '#fff',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {detected ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Added to Chrome
              </>
            ) : (
              'Add to Chrome'
            )}
          </div>
          <div className="text-[11.5px] text-ink-muted mt-2.5">
            {detected
              ? 'Great, we detected the install.'
              : 'Click "Add to Chrome" in the new tab that just opened.'}
          </div>
        </div>
      </div>
    </StepFrame>
  );
}

// ============================================================
// Pin step
// ============================================================

function PinStep({ onAdvance, detected, setDetected, stepIdx, stepCount }: DetectedStepProps) {
  // Pinning isn't programmatically detectable from a web page, so the only
  // signals we have are (a) the user clicking through the simulated toolbar in
  // ChromeToolbarVisual or (b) confirming via this button. Both flip
  // `detected`, which advances them.
  const handleConfirm = () => {
    track('onboard_pin_confirmed');
    if (!detected) setDetected(true);
    onAdvance();
  };
  return (
    <>
      {!detected && (
        <OnboardingArrow label={<>Click the puzzle piece<br />up here to find the<br /><span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontStyle: 'normal', fontWeight: 800, background: 'var(--accent-soft)', color: 'var(--accent-deep)', padding: '1px 6px', borderRadius: 4, fontSize: '0.92em' }}>go/</span> icon, then pin it</>} />
      )}
      <StepFrame
        eyebrow={`Step ${stepIdx + 1} of ${stepCount}`}
        title="Pin it to your toolbar."
        body="So you can always get to it. Click the puzzle-piece icon in Chrome's toolbar, then click the pin next to Tidy Go Links."
        primaryCta={
          <C2Button variant="accent" size="lg" onClick={handleConfirm}>
            {detected ? 'Continue →' : "I've pinned it →"}
          </C2Button>
        }
        detected={detected}
        detectedLabel="Pinned to toolbar"
      >
        <ChromeToolbarVisual pinned={detected} setPinned={setDetected} />
      </StepFrame>
    </>
  );
}

// ============================================================
// SignIn step (extension)
// ============================================================

function SignInStep({ user, onAdvance, detected, setDetected, stepIdx, stepCount, onGoToPin }: DetectedStepProps & { onGoToPin: () => void }) {
  // Fast skip: v3.6+ extensions sync the website's token into chrome.storage
  // on every tidygolinks.com page load, so by the time the user clicks
  // through earlier steps and lands here, the extension is already signed in
  // and there's nothing for them to do. Read the attribute synchronously to
  // avoid a render flash before auto-advancing.
  const [skipping] = useState(() => !detected && extensionSignInState() === 'yes');

  useEffect(() => {
    if (skipping) {
      onAdvance();
      return;
    }
    if (detected) return;
    const stepOpenedAt = Date.now();
    let cancelled = false;

    // Primary signal (v3.6+): content.js sets data-tidygolinks-ext-signed-in
    // on <html> as soon as chrome.storage holds a token, and updates it live
    // via chrome.storage.onChanged. A cheap local poll catches the flip the
    // moment the user signs in via the popup — no /auth/me round-trip needed.
    const attrTick = () => {
      if (cancelled) return;
      if (extensionSignInState() === 'yes') {
        setDetected(true);
        return;
      }
      setTimeout(attrTick, 250);
    };
    attrTick();

    // Fallback (≤v3.5): old extensions don't set the attribute, so poll
    // /auth/me for a fresh extension_last_seen. The popup pings
    // /auth/extension-ping on init once signed in. We require the ping to
    // be STRICTLY AFTER this step opened AND fresher than what was on
    // record at mount, so a stale background ping from another machine —
    // or a pre-existing recent ping from this browser — doesn't false-fire.
    let lastSeenSnapshot: number | null = null;
    const meTick = async () => {
      if (cancelled) return;
      try {
        const me = await api.me();
        const lastSeen = me.extension_last_seen ? new Date(me.extension_last_seen).getTime() : 0;
        if (lastSeenSnapshot === null) {
          lastSeenSnapshot = lastSeen;
        } else if (lastSeen > stepOpenedAt && lastSeen > lastSeenSnapshot) {
          setDetected(true);
          return;
        }
      } catch { /* offline / 401 — let the caller fail elsewhere */ }
      if (!cancelled) setTimeout(meTick, 3_000);
    };
    void meTick();

    return () => {
      cancelled = true;
    };
  }, [skipping, detected, setDetected, onAdvance]);

  if (skipping) return null;

  return (
    <>
      {!detected && (
        <OnboardingArrow label={<>Click the <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontStyle: 'normal', fontWeight: 800, background: 'var(--accent-soft)', color: 'var(--accent-deep)', padding: '1px 6px', borderRadius: 4, fontSize: '0.92em' }}>go/</span> icon<br />to sign in</>} />
      )}
      <StepFrame
        eyebrow={`Step ${stepIdx + 1} of ${stepCount}`}
        title="Sign in to the extension."
        body="Click the go/ icon in your toolbar and sign in with your account."
        primaryCta={
          <DisabledHint reason={!detected ? 'Sign in to the extension first' : null}>
            <C2Button
              variant={detected ? 'accent' : 'soft'}
              size="lg"
              onClick={detected ? onAdvance : undefined}
              disabled={!detected}
              style={!detected ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            >
              Continue →
            </C2Button>
          </DisabledHint>
        }
        detected={detected}
        detectedLabel="Extension signed in"
      >
        <ExtensionPopupVisual user={user} signedIn={detected} setSignedIn={setDetected} onGoToPin={onGoToPin} />
      </StepFrame>
    </>
  );
}

// ============================================================
// First link step — REAL POST /links
// ============================================================

interface FirstLinkProps extends StepProps {
  created: { slug: string; url: string } | null;
  setCreated: (v: { slug: string; url: string } | null) => void;
}

function FirstLinkStep({ user, onAdvance, stepIdx, stepCount, created, setCreated }: FirstLinkProps) {
  const [slug] = useState('');
  const [url] = useState('');
  const userInitial = user.name?.[0]?.toUpperCase() ?? user.email[0]?.toUpperCase() ?? 'R';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canCreate = slug.trim().length > 0 && url.trim().length > 0 && !saving;

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    setError(null);
    try {
      const result = await api.createLink({ slug: slug.trim(), url: url.trim() });
      setCreated({ slug: result.slug, url: result.url });
    } catch (err) {
      setError((err as Error).message || 'Could not create link');
    } finally {
      setSaving(false);
    }
  };

  const STARTERS = [
    { slug: 'handbook', desc: 'Company wiki' },
    { slug: 'roadmap', desc: 'This quarter' },
    { slug: 'oncall', desc: 'Who\'s on-call' },
    { slug: 'calendar', desc: 'Team calendar' },
  ];

  return (
    <>
      {!created && (
        <OnboardingArrow label={<>Click the <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontStyle: 'normal', fontWeight: 800, background: 'var(--accent-soft)', color: 'var(--accent-deep)', padding: '1px 6px', borderRadius: 4, fontSize: '0.92em' }}>go/</span> icon<br />to create your first<br />go/ link</>} />
      )}
      <StepFrame
      eyebrow={`Step ${stepIdx + 1} of ${stepCount}`}
      title="Create your first go/ link."
      body="From the extension itself — that's where you'll live most of the time. The dashboard is for managing; the extension is for doing."
      primaryCta={
        <C2Button variant="accent" size="lg" onClick={onAdvance}>Continue →</C2Button>
      }
      detected={!!created}
      detectedLabel={created ? `go/${created.slug} created` : ''}
    >
      <div className="flex gap-10 flex-wrap items-start">
        {/* Browser + extension popup: shows the user where to find the real
            extension icon (top right of their actual Chrome toolbar). */}
        <div className="relative" style={{ width: 380 }}>
          <div
            className="bg-surface overflow-hidden"
            style={{
              borderRadius: 10,
              border: '1px solid var(--color-hairline)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            {/* Title bar */}
            <div
              className="flex items-center"
              style={{
                gap: 10,
                padding: '9px 12px',
                background: 'oklch(0.94 0.01 85)',
                borderBottom: '1px solid var(--color-hairline)',
              }}
            >
              <div className="flex" style={{ gap: 5 }}>
                <div className="w-[9px] h-[9px] rounded-full" style={{ background: 'oklch(0.72 0.17 25)' }} />
                <div className="w-[9px] h-[9px] rounded-full" style={{ background: 'oklch(0.85 0.16 85)' }} />
                <div className="w-[9px] h-[9px] rounded-full" style={{ background: 'oklch(0.76 0.16 145)' }} />
              </div>
            </div>
            {/* URL bar with extension icon on the right */}
            <div className="flex items-center" style={{ gap: 6, padding: '9px 10px' }}>
              <div
                className="flex-1 font-mono text-ink-muted"
                style={{
                  background: 'var(--color-bg-alt)',
                  border: '1px solid var(--color-hairline)',
                  borderRadius: 999,
                  padding: '5px 11px',
                  fontSize: 10,
                }}
              >
                chrome://newtab
              </div>
              <button
                className="font-mono font-extrabold flex items-center justify-center cursor-pointer relative"
                style={{
                  width: 30,
                  height: 30,
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-deep)',
                  border: '2px solid var(--accent-solid)',
                  borderRadius: 7,
                  fontSize: 11,
                  boxShadow: '0 0 0 4px color-mix(in oklch, var(--accent-solid) 13%, transparent)',
                  animation: created ? undefined : 'c2pulseRing 1.8s ease-in-out infinite',
                }}
              >
                go/
                {!created && (
                  <div
                    className="absolute font-semibold whitespace-nowrap"
                    style={{
                      top: -30,
                      right: -6,
                      background: 'var(--color-ink)',
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 10.5,
                      animation: 'c2bounce 1.8s ease-in-out infinite',
                    }}
                  >
                    Click your go/ icon
                    <span
                      className="absolute"
                      style={{
                        bottom: -4,
                        right: 10,
                        width: 0,
                        height: 0,
                        borderLeft: '4px solid transparent',
                        borderRight: '4px solid transparent',
                        borderTop: '4px solid var(--color-ink)',
                      }}
                    />
                  </div>
                )}
              </button>
              <div
                className="rounded-full flex items-center justify-center font-bold"
                style={{
                  width: 30,
                  height: 30,
                  background: 'var(--color-lemon)',
                  color: 'var(--color-lemon-deep)',
                  fontSize: 11,
                }}
              >
                {userInitial}
              </div>
            </div>
          </div>

          {/* Extension popup (floating below the icon) */}
          <div
            className="bg-surface relative"
            style={{
              marginTop: 14,
              marginLeft: 'auto',
              width: 320,
              borderRadius: 20,
              border: '1px solid var(--color-hairline-strong)',
              boxShadow: 'var(--shadow-lg)',
              overflow: 'hidden',
              animation: 'c2fadeSlide 260ms ease-out',
            }}
          >
            {/* Pointer triangle pointing up to where the icon is */}
            <div
              className="absolute"
              style={{
                top: -7,
                right: 60,
                width: 12,
                height: 12,
                background: 'var(--accent-tint)',
                borderTop: '1px solid var(--color-hairline-strong)',
                borderLeft: '1px solid var(--color-hairline-strong)',
                transform: 'rotate(45deg)',
              }}
            />
            <div
              className="flex items-center"
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--color-hairline)',
                background: 'var(--accent-tint)',
                gap: 10,
              }}
            >
              <div
                className="flex items-center justify-center font-mono font-extrabold"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-deep)',
                  fontSize: 11,
                }}
              >
                go/
              </div>
              <span className="text-[13px] font-bold text-ink">Tidy Go Links</span>
            </div>

          {created ? (
            <div className="text-center" style={{ padding: '26px 16px' }}>
              <div
                className="rounded-full mx-auto flex items-center justify-center mb-3.5"
                style={{
                  width: 52,
                  height: 52,
                  background: 'oklch(0.92 0.08 150)',
                  color: 'oklch(0.34 0.1 150)',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div
                className="font-display font-bold text-ink mb-1"
                style={{ fontSize: 18 }}
              >
                go/{created.slug} is live.
              </div>
              <div className="text-xs text-ink-soft mb-3.5">
                Type <span className="font-mono font-semibold text-ink">go/{created.slug}</span> in any browser to open it.
              </div>
              <div
                className="font-mono text-ink-soft overflow-hidden text-ellipsis whitespace-nowrap"
                style={{
                  padding: '8px 10px',
                  background: 'var(--color-bg-alt)',
                  borderRadius: 8,
                  fontSize: 11.5,
                }}
              >
                → {created.url}
              </div>
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <div
                className="font-bold uppercase mb-3"
                style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.06em' }}
              >
                New go/ link
              </div>
              <label className="block text-[11px] font-semibold text-ink-soft mb-1">Destination URL</label>
              <input
                value={url}
                readOnly
                tabIndex={-1}
                placeholder="https://..."
                style={{ ...inputStyle, padding: '7px 10px', fontSize: 12, pointerEvents: 'none' }}
                className="mb-2.5"
              />
              <label className="block text-[11px] font-semibold text-ink-soft mb-1">Your go/ link</label>
              <div
                className="flex items-stretch overflow-hidden mb-3.5"
                style={{ border: '1px solid var(--color-hairline-strong)', borderRadius: 8 }}
              >
                <span
                  className="flex items-center font-mono font-bold"
                  style={{
                    padding: '0 10px',
                    background: 'var(--accent-soft)',
                    color: 'var(--accent-deep)',
                    fontSize: 12,
                  }}
                >
                  go/
                </span>
                <input
                  value={slug}
                  readOnly
                  tabIndex={-1}
                  placeholder="roadmap"
                  className="flex-1 border-0 outline-none bg-transparent font-mono font-semibold text-ink"
                  style={{ padding: '7px 10px', fontSize: 12, pointerEvents: 'none' }}
                />
              </div>
              {error && (
                <p className="text-[12px] mb-2 m-0" style={{ color: 'var(--color-peach-deep)' }}>{error}</p>
              )}
              <button
                disabled={!canCreate}
                onClick={handleCreate}
                className="w-full font-semibold cursor-pointer"
                style={{
                  padding: '9px 12px',
                  background: canCreate ? 'var(--accent-solid)' : 'var(--color-bg-alt)',
                  color: canCreate ? '#fff' : 'var(--color-ink-muted)',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: canCreate ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Saving…' : 'Save go/ link'}
              </button>
            </div>
          )}
          </div>
        </div>

        <div className="flex-1 max-w-[320px]">
          <div
            className="font-bold uppercase mb-3.5"
            style={{ fontSize: 12, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
          >
            Good first links
          </div>
          <div className="flex flex-col gap-2.5">
            {STARTERS.map(s => (
              <div
                key={s.slug}
                className="flex items-center gap-2.5 text-left bg-surface"
                style={{
                  padding: '10px 12px',
                  border: '1px solid var(--color-hairline)',
                  borderRadius: 8,
                }}
              >
                <span className="font-mono font-semibold text-ink text-[12.5px]">
                  <span className="text-ink-muted">go/</span>{s.slug}
                </span>
                <span className="text-[11.5px] text-ink-muted ml-auto text-right">{s.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes c2pulseRing {
          0%, 100% { box-shadow: 0 0 0 4px color-mix(in oklch, var(--accent-solid) 13%, transparent); }
          50% { box-shadow: 0 0 0 8px color-mix(in oklch, var(--accent-solid) 7%, transparent); }
        }
        @keyframes c2bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes c2fadeSlide {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </StepFrame>
    </>
  );
}

// ============================================================
// Invite step (creator only)
// ============================================================

function InviteStep({ user, onAdvance, stepIdx, stepCount }: StepProps) {
  const domain = user.email.split('@')[1] ?? 'your-company.com';
  const isPersonalDomain = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'].includes(domain.toLowerCase());
  const body = isPersonalDomain
    ? 'Send direct invites so your team members can join your workspace.'
    : `Anyone who joins with an @${domain} email gets access automatically. Or send them a direct invite so they don't miss it.`;
  return (
    <StepFrame
      eyebrow={`Step ${stepIdx + 1} of ${stepCount}`}
      title="Invite your team."
      body={body}
      primaryCta={<C2Button variant="accent" size="lg" onClick={onAdvance}>Finish setup →</C2Button>}
    >
      <InviteStepBody />
    </StepFrame>
  );
}

// ============================================================
// Done step
// ============================================================

function DoneStep({ mode, onGoToDashboard }: { mode: 'creator' | 'joiner' | 'fix'; onGoToDashboard: () => void }) {
  const title = mode === 'joiner' ? "You're in." : mode === 'fix' ? 'All set on this browser.' : "You're all set.";
  const body = mode === 'fix'
    ? "Your extension's wired up here too. Type go/ anywhere in this browser and you're back in business."
    : (
      <>
        When you're on a website you use frequently, click the Tidy Go Links extension icon to create a new{' '}
        <span
          className="font-mono font-semibold text-ink"
          style={{ background: 'var(--color-bg-alt)', padding: '2px 8px', borderRadius: 5 }}
        >
          go/
        </span>{' '}
        link. Have fun and reach out to us at{' '}
        <a
          href="mailto:hello@tidygolinks.com"
          style={{ color: 'var(--accent-deep)', textDecoration: 'underline' }}
        >
          hello@tidygolinks.com
        </a>{' '}
        if you have any questions.
      </>
    );
  return (
    <div
      className="mx-auto text-center relative"
      style={{ maxWidth: 680, padding: '100px 48px' }}
    >
      <div
        className="absolute"
        style={{ top: 60, left: -40, width: 120, height: 120, borderRadius: '50%', background: 'var(--color-peach)', opacity: 0.5 }}
      />
      <div
        className="absolute"
        style={{ bottom: 80, right: -30, width: 90, height: 90, borderRadius: '50%', background: 'var(--color-mint)', opacity: 0.5 }}
      />
      <div
        className="absolute"
        style={{ top: 200, right: 40, width: 60, height: 60, borderRadius: '50%', background: 'var(--color-lilac)', opacity: 0.5 }}
      />
      <div className="relative">
        <div
          className="mx-auto mb-7 flex items-center justify-center font-mono font-extrabold"
          style={{
            width: 88,
            height: 88,
            borderRadius: 22,
            background: 'var(--accent-soft)',
            color: 'var(--accent-deep)',
            fontSize: 28,
            boxShadow: '0 10px 30px -12px color-mix(in oklch, var(--accent-solid) 33%, transparent)',
          }}
        >
          go/
        </div>
        <h1
          className="font-display font-bold text-ink m-0 mb-4"
          style={{ fontSize: 52, letterSpacing: '-0.025em', lineHeight: 1.05 }}
        >
          {title}
        </h1>
        <p className="text-lg text-ink-soft leading-[1.5] m-0 mb-9 mx-auto" style={{ maxWidth: 460 }}>
          {body}
        </p>
        <C2Button variant="accent" size="lg" onClick={onGoToDashboard}>
          Go to my dashboard →
        </C2Button>
      </div>
    </div>
  );
}

// ============================================================
// Controller
// ============================================================

interface DetectedFlags {
  installed: boolean;
  pinned: boolean;
  signedIn: boolean;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { stepId: urlStepId } = useParams<{ stepId?: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Returning user routed here from the dashboard's extension gate. Slim
  // step list, and we don't re-flip onboarding_complete on finish.
  const isFixExtension = typeof window !== 'undefined'
    && new URLSearchParams(location.search).get('fix') === 'extension';

  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<'creator' | 'joiner'>('creator');
  const [detected, setDetected] = useState<DetectedFlags>({ installed: false, pinned: false, signedIn: false });
  const [firstLink, setFirstLink] = useState<{ slug: string; url: string } | null>(null);

  useEffect(() => {
    if (!getToken()) {
      window.location.href = '/login';
      return;
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [me, org] = await Promise.all([api.me(), api.organization().catch(() => null)]);
        setUser(me);
        // First user in the workspace = creator. Otherwise joiner.
        const memberCount = org?.members?.length ?? 1;
        setRole(memberCount <= 1 ? 'creator' : 'joiner');
      } catch (err) {
        const status = (err as { status?: number }).status;
        if (status === 401 || status === 403) {
          window.location.href = '/login';
          return;
        }
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const isPersonalSignup = !!user && (
    !!user.workspace?.is_personal
    || ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com', 'proton.me', 'protonmail.com']
        .includes((user.email.split('@')[1] ?? '').toLowerCase())
  );
  const baseSteps = isFixExtension
    ? FIX_EXTENSION_STEPS
    : (role === 'creator' ? CREATOR_STEPS : JOINER_STEPS);
  const steps = isPersonalSignup ? baseSteps.filter(s => s.id !== 'invite') : baseSteps;
  const isDone = urlStepId === 'done';
  const stepIdx = isDone ? steps.length : steps.findIndex(s => s.id === urlStepId);
  const currentStep = stepIdx >= 0 && stepIdx < steps.length ? steps[stepIdx] : undefined;

  // URL is the source of truth for which step is shown. If the URL has no
  // stepId, or names a step that doesn't exist in this user's flow (e.g. a
  // personal-domain user landing on /onboarding/invite), redirect to the
  // first step. Wait for user data first since `steps` depends on role +
  // signup type.
  useEffect(() => {
    if (loading || !user) return;
    if (isDone) return;
    if (!urlStepId || stepIdx === -1) {
      navigate(`/onboarding/${steps[0].id}${location.search}`, { replace: true });
    }
  }, [loading, user, urlStepId, stepIdx, isDone, steps, location.search, navigate]);

  const markDone = (id: string) => setCompleted(prev => new Set(prev).add(id));

  const goToStep = (idx: number) => {
    if (idx >= steps.length) {
      navigate(`/onboarding/done${location.search}`);
    } else if (idx >= 0) {
      navigate(`/onboarding/${steps[idx].id}${location.search}`);
    }
  };

  const advance = () => {
    if (currentStep) markDone(currentStep.id);
    goToStep(stepIdx + 1);
  };

  const updateDetected = (key: keyof DetectedFlags) => (v: boolean) => {
    setDetected(prev => ({ ...prev, [key]: v }));
    if (v && currentStep) markDone(currentStep.id);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center text-ink-soft">
        Loading…
      </div>
    );
  }

  const finishOnboarding = async () => {
    // Fix-extension mode: user already finished onboarding once; just bounce
    // them back to the dashboard. Don't re-mark onboarding_complete (it's
    // already true) and don't fire the API call.
    if (!isFixExtension) {
      // Best-effort — if the API call fails the user still leaves; the
      // dashboard's onboarding gate will simply send them back here next
      // time, which is the right behavior on a transient failure.
      try {
        await api.markOnboardingComplete();
      } catch { /* swallow — see comment above */ }
    }
    navigate('/dashboard');
  };

  const renderStep = (): ReactNode => {
    if (isDone || !currentStep) {
      return <DoneStep mode={isFixExtension ? 'fix' : role} onGoToDashboard={finishOnboarding} />;
    }
    const common: StepProps = { user, onAdvance: advance, stepIdx, stepCount: steps.length };
    switch (currentStep.id) {
      case 'workspace': return <WorkspaceStep {...common} />;
      case 'welcome': return <WelcomeStep {...common} />;
      case 'install':
        return <InstallStep {...common} detected={detected.installed} setDetected={updateDetected('installed')} />;
      case 'pin':
        return <PinStep {...common} detected={detected.pinned} setDetected={updateDetected('pinned')} />;
      case 'signin': {
        const pinIdx = steps.findIndex(s => s.id === 'pin');
        return <SignInStep {...common} detected={detected.signedIn} setDetected={updateDetected('signedIn')} onGoToPin={() => goToStep(pinIdx)} />;
      }
      case 'firstlink':
        return <FirstLinkStep {...common} created={firstLink} setCreated={(v) => { setFirstLink(v); if (v) markDone('firstlink'); }} />;
      case 'invite':
        return <InviteStep {...common} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-bg font-body">
      <ProgressRail
        steps={steps}
        currentIdx={stepIdx}
        completed={isDone ? new Set(steps.map(s => s.id)) : completed}
        onJump={i => {
          // Only allow jumping to completed steps or the next step
          if (i <= stepIdx + 1) {
            goToStep(i);
          }
        }}
        user={user}
      />
      <main className="flex-1 min-w-0 relative">
        {renderStep()}
      </main>
    </div>
  );
}
